/**
 * PO status roll-up prompt (card i-salOBh3pft).
 *
 * Client feedback 2026-09-30: when every line item on a PO has been changed to
 * the same status, ask whether to set the PO to that status too — never change
 * the PO silently:
 *
 *   "All N line items are now <status>. Set PO-xxxx to <status> too?"
 *
 * Replaces the Received-only window.confirm that lived in PurchaseOrderSidebar
 * and PurchaseOrderDetails (a data-watching effect that only ran while one of
 * those two surfaces was open). Now every web line-status entry point calls
 * promptForPurchaseOrders(poIds) after its write succeeds:
 *   - ClickablePurchaseOrderLineItemStatusBadge (PO sidebar Items tab, PO
 *     details, lot PO tab, lot pick tickets, job PO tab)
 *   - ClickableLineItemStatusBadgeOptimistic (PO tracker line tables, slab tab)
 *   - LineItemBatchEditToolbar saves (PO sidebar, PO details)
 *   - BatchActionsCard line actions (PO tracker, lot PO tab)
 * NOT called by the PO → lines cascade dialog (the user just chose the PO
 * status) or by Receive Items (the receive service already sets the PO
 * Received when every line is fully received, note 0498).
 *
 * - getPoRollupStatus (utils/poStatusRollup.ts) is the rule.
 * - The PO + its lines are read fresh from the server, so the check sees the
 *   state AFTER the change.
 * - PoStatusRollupProvider is mounted once in the web layout (routes.tsx), so
 *   the prompt survives the row / batch bar that triggered it unmounting.
 * - "Set PO" writes ONLY the PO header through
 *   usePurchaseOrderStatusManagementOptimistic — the same write the old
 *   Received prompt used. It does not open the PO → lines cascade dialog and
 *   does not touch the lines.
 */

import { ClipboardList, Loader2 } from 'lucide-react';
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

import { purchaseOrderLineItemsExtensions } from '../../api/extensions/purchase_order_line_items-business-logic-extension.js';
import { purchaseOrdersExtensions } from '../../api/extensions/purchase_orders-business-logic-extension.js';
import { usePurchaseOrderStatusesList } from '../../api/generated_hooks/generated-purchase_order_statuses-hooks.js';
import type { PurchaseOrders } from '../../api/generated_schemas/generated-purchase_orders-schema.js';
import { usePurchaseOrderStatusManagementOptimistic } from '../../api/non_generated_business_hooks/purchase_order_business_hooks/purchase_order_status_management_business_hooks.js';
import { useAuth } from '../../hooks/useAuth.js';
import { BoulderDialog } from '../ui/boulder-dialog.js';
import { Button } from '../ui/button.js';

import { getPoRollupStatus, type RollupPoStatus } from './utils/poStatusRollup.js';

interface PoStatusOption extends RollupPoStatus {
  name: string;
  color?: string | null;
}

interface PoRollupCandidate {
  purchaseOrderId: string;
  purchaseOrder: PurchaseOrders;
  lineCount: number;
  /** The status every line now shares (line status name). */
  lineStatusName: string;
  target: PoStatusOption;
}

type PromptForPurchaseOrders = (purchaseOrderIds: ReadonlyArray<string | null | undefined>) => void;

// Default is a no-op, so a component rendered outside the provider never prompts.
const PoStatusRollupContext = createContext<PromptForPurchaseOrders>(() => {});

/**
 * Returns promptForPurchaseOrders(poIds): call it AFTER a successful line
 * status change with the changed lines' purchase_order_id values.
 */
export function usePoStatusRollup(): PromptForPurchaseOrders {
  return useContext(PoStatusRollupContext);
}

export function PoStatusRollupProvider({ children }: { children: ReactNode }) {
  const { organizationId } = useAuth();
  const { data: poStatuses } = usePurchaseOrderStatusesList({
    filters: organizationId ? { organization_id: organizationId } : {},
  });
  const { updatePurchaseOrderStatus } = usePurchaseOrderStatusManagementOptimistic();
  const [queue, setQueue] = useState<PoRollupCandidate[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  // Latest statuses, read inside the stable callback.
  const statusesRef = useRef(poStatuses);
  statusesRef.current = poStatuses;

  const promptForPurchaseOrders = useCallback<PromptForPurchaseOrders>((purchaseOrderIds) => {
    const statuses = (statusesRef.current ?? []) as PoStatusOption[];
    if (statuses.length === 0) {return;}
    const ids = Array.from(new Set(purchaseOrderIds.filter((id): id is string => !!id)));
    for (const id of ids) {
      Promise.all([
        purchaseOrdersExtensions.getById(id),
        purchaseOrderLineItemsExtensions.getAll({ filters: { purchase_order_id: id }, limit: 500 }),
      ])
        .then(([purchaseOrder, lines]) => {
          if (!purchaseOrder) {return;}
          const target = getPoRollupStatus(
            purchaseOrder.status_id,
            lines.map((l) => ({ code: l.trig_status_code, name: l.trig_status_name })),
            statuses,
          );
          if (!target) {return;}
          const candidate: PoRollupCandidate = {
            purchaseOrderId: id,
            purchaseOrder,
            lineCount: lines.length,
            lineStatusName: lines[0].trig_status_name || target.name,
            target,
          };
          setQueue((q) => (q.some((c) => c.purchaseOrderId === id) ? q : [...q, candidate]));
        })
        // Read-only check: a failure just means no prompt.
        .catch((err) => console.error('PO status roll-up check failed:', err));
    }
  }, []);

  const current = queue[0];
  const next = () => setQueue((q) => q.slice(1));

  const handleConfirm = async () => {
    if (!current) {return;}
    setIsProcessing(true);
    try {
      await updatePurchaseOrderStatus(
        {
          purchaseOrderId: current.purchaseOrderId,
          statusId: current.target.id,
          statusName: current.target.name,
          statusColor: current.target.color ?? null,
        },
        current.purchaseOrder,
      );
    } catch (err) {
      // The optimistic hook already rolled back + toasted the error.
      console.error('Failed to set PO status from line roll-up:', err);
    } finally {
      setIsProcessing(false);
      next();
    }
  };

  const poLabel = current?.purchaseOrder.po_number || 'this purchase order';

  return (
    <PoStatusRollupContext.Provider value={promptForPurchaseOrders}>
      {children}
      {current && (
        <BoulderDialog
          key={current.purchaseOrderId}
          open
          onOpenChange={(o) => { if (!o && !isProcessing) { next(); } }}
          tone="indigo"
          width="md"
          icon={<ClipboardList className="h-5 w-5" />}
          title="Update the purchase order?"
          description={
            <>
              All {current.lineCount} line item{current.lineCount === 1 ? ' is' : 's are'} now{' '}
              <span className="font-semibold text-foreground">{current.lineStatusName}</span>.
              {' '}Set <span className="font-semibold text-foreground">{poLabel}</span> to{' '}
              <span className="font-semibold text-foreground">{current.target.name}</span> too?
            </>
          }
        >
          <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={next} disabled={isProcessing}>
              Not now
            </Button>
            <Button onClick={handleConfirm} disabled={isProcessing}>
              {isProcessing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Set PO to {current.target.name}
            </Button>
          </div>
        </BoulderDialog>
      )}
    </PoStatusRollupContext.Provider>
  );
}
